import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  fullScreen?: boolean;
  dismissable?: boolean;
  noScroll?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  fullScreen = false,
  dismissable = true,
  noScroll = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // Скроллится #root (body всегда overflow:hidden — см. index.css), поэтому
  // фон под модалкой блокируем именно на нем.
  useEffect(() => {
    const root = document.getElementById('root');
    const lock = () => { if (root) root.style.overflow = 'hidden'; };
    const unlock = () => { if (root) root.style.overflow = ''; };

    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
      lock();
    } else {
      setAnimating(false);
      unlock();
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }

    return unlock;
  }, [isOpen]);

  if (!visible) return null;

  // Портал в body: иначе модалка попадает в stacking context страницы
  // (напр. `relative z-10` в профиле) и оказывается под нижней навигацией.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ${
          animating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={dismissable ? onClose : undefined}
      />

      {/* Modal content */}
      <div
        className={`relative bg-white w-full flex flex-col modal-sheet overflow-hidden transition-all duration-300 ease-out ${
          animating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        } ${
          fullScreen
            ? 'h-full rounded-none'
            : 'rounded-t-[20px] sm:rounded-[20px] sm:max-w-lg sm:mx-4'
        }`}
      >
        {/* Header */}
        {title && (
          <div className="flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
            <h2 className="text-base font-bold text-text-primary">{title}</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-background-secondary rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Body */}
        <div className={`flex-1 min-h-0 ${noScroll ? 'overflow-hidden' : 'overflow-y-auto'} px-4 py-3 ${footer ? '' : 'pb-[max(0.75rem,env(safe-area-inset-bottom))]'}`}>{children}</div>

        {/* Footer */}
        {footer && (
          <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
